import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import { BroadcastMessageService } from '../../../../services/broadcast/broadcast.service';
import { RecipientsService } from '../../../../services/campaign/recipients.service';
import { smsParts } from '../../../../../utils/sms';

/**
 * The wizard's unique audience: how many people the chosen organizations,
 * tags and segments reach, counted once each by the backend
 * (POST preview/recipient-count). Recounts only when the selection changes,
 * and mirrors the answer into RecipientsService.totalRecipientsCount.
 */
@Injectable({ providedIn: 'root' })
export class WizardAudienceService {
  private broadcast = inject(BroadcastMessageService);
  private recipientsService = inject(RecipientsService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Unique recipients, or null while unknown. */
  count: number | null = null;
  loading = false;
  failed = false;

  private key = '';
  private sub?: Subscription;

  /** True when no group is selected. */
  get empty(): boolean {
    const { organizations, tags, segments } = this.recipientsService.recipients;
    return !organizations.length && !tags.length && !segments.length;
  }

  /** Count again if the selection changed since the last answer. */
  refresh(force = false) {
    const payload = this.payload();
    const key = JSON.stringify(payload);
    if (!force && key === this.key && (this.count !== null || this.loading)) return;

    this.key = key;
    this.sub?.unsubscribe();

    if (!payload.organizationIds.length && !payload.tagIds.length && !payload.segmentIds.length) {
      this.count = 0;
      this.loading = false;
      this.failed = false;
      this.recipientsService.totalRecipientsCount = 0;
      return;
    }
    if (!this.isBrowser) return;

    this.loading = true;
    this.failed = false;
    this.sub = this.broadcast.fetchCampaignMessageRecipientCount(payload).subscribe({
      next: (response) => {
        this.loading = false;
        this.count = Number(response?.totalRecipients) || 0;
        this.recipientsService.totalRecipientsCount = this.count;
      },
      error: () => {
        this.loading = false;
        this.failed = true;
        this.count = null;
        // Ask again next time instead of keeping the failure.
        this.key = '';
      },
    });
  }

  reset() {
    this.sub?.unsubscribe();
    this.key = '';
    this.count = null;
    this.loading = false;
    this.failed = false;
  }

  /**
   * SMS this copy costs for the whole audience at one send: parts × people.
   * Null until the audience is known.
   */
  unitsPerSend(body: string | null | undefined): number | null {
    if (this.count === null) return null;
    return smsParts(body).parts * this.count;
  }

  private payload() {
    const { organizations, tags, segments } = this.recipientsService.recipients;
    const ids = (items: { uuid?: string | null }[]) =>
      items.map((item) => item?.uuid).filter((uuid): uuid is string => !!uuid).map(String);
    return {
      phoneNumbers: [] as string[],
      contactIds: [] as string[],
      tagIds: ids(tags),
      organizationIds: ids(organizations as { uuid?: string | null }[]),
      segmentIds: ids(segments),
    };
  }
}
